import { createAsync, useNavigate } from '@solidjs/router';
import { ActivityIndicator, Container, Picture } from '@troon/ui';
import { Match, Show, Suspense, Switch, createEffect, createSignal } from 'solid-js';
import { Title, Meta } from '@solidjs/meta';
import { ApiError, cachedGet } from '@troon/api-client';
import { useUser } from '../../../../../../providers/user';
import { getConfigValue } from '../../../../../../modules/config';
import { Error404 } from '../../../../../../graphql';
import { Waiver } from '../../../../../../partials/waiver';
import {
	InviteDecisionStep,
	PaymentOptionsLoading,
	PaymentStep,
} from '../../../../../../components/reservation/invite';
import type { WaiverHandler } from '../../../../../../partials/waiver';
import type { ParentProps } from 'solid-js';
import type { RouteDefinition, RouteSectionProps } from '@solidjs/router';
import type { PostBookingRedemptionPreview } from '@troon/api-client';
import type { ReservationInvite } from '../../../../../../components/reservation/invite';

type RedemptionPreviewState =
	| { status: 'ready'; preview: PostBookingRedemptionPreview }
	| { status: 'error'; message: string }
	| { status: 'unavailable' };
type AvailableRedemptionPreviewState = Exclude<RedemptionPreviewState, { status: 'unavailable' }>;

export default function JoinReservation(props: RouteSectionProps) {
	const user = useUser();
	const [waiver, setWaiver] = createSignal<WaiverHandler>();
	const data = createAsync(
		async () => {
			if (!props.params.reservationId || !props.params.shareInviteId) {
				return undefined;
			}
			try {
				return await getReservationInvite({
					path: {
						reservationId: props.params.reservationId,
						shareInviteId: props.params.shareInviteId,
					},
				});
			} catch (error) {
				if (error instanceof ApiError && error.statusCode === 404) {
					throw new Error404(error.message || 'Reservation invitation not found');
				}
				throw error;
			}
		},
		{ deferStream: true },
	);

	const currentUserId = () => user()?.id;

	return (
		<div class="relative flex min-h-svh items-center justify-center overflow-hidden py-10 md:py-16">
			<Suspense>
				<Title>
					{data()?.owner.firstName} {data()?.owner.lastName} has invited you to a tee time! | Golf with Access
				</Title>
				<Picture
					src={data()?.facility.hero}
					alt=""
					preload
					class="absolute inset-0 -z-10 h-svh w-screen self-stretch brightness-75"
					width={1200}
					height={1200}
					sizes="(min-width 768px) 100vw, 200vw"
				/>
			</Suspense>

			<Meta
				name="og:image"
				content={`https://${getConfigValue('MAP_HOST')}/reservation/${props.params.reservationId}/join/${props.params.shareInviteId}/og.jpg`}
			/>
			<Meta name="description" content="Log in or sign up for Access to accept this invitation." />

			<Container size="sm">
				<div class="overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-black/10">
					<Suspense
						fallback={
							<div class="grid place-items-center p-6 md:p-10">
								<ActivityIndicator>Loading invitation</ActivityIndicator>
							</div>
						}
					>
						<Show when={data()} keyed>
							{(reservation) => (
								<InviteFlow
									reservation={reservation}
									reservationId={props.params.reservationId!}
									shareInviteId={props.params.shareInviteId!}
									userId={currentUserId()}
									waiver={waiver()}
								/>
							)}
						</Show>
					</Suspense>
				</div>
			</Container>
			<Waiver ref={setWaiver} />
		</div>
	);
}

function InviteFlow(props: {
	reservation: ReservationInvite;
	reservationId: string;
	shareInviteId: string;
	userId?: string;
	waiver?: WaiverHandler;
}) {
	const navigate = useNavigate();
	const [paymentReservationUserId, setPaymentReservationUserId] = createSignal<string>();
	const [redemptionPreviewRetry, setRedemptionPreviewRetry] = createSignal(0);
	const reservationHref = () => `/reservation/${props.reservationId}`;
	const availableRedemptionPreview = () => {
		const preview = redemptionPreview();
		return preview?.status === 'unavailable' ? undefined : preview;
	};

	const redemptionPreview = createAsync(
		async (): Promise<RedemptionPreviewState | undefined> => {
			redemptionPreviewRetry();

			const reservationUserId = paymentReservationUserId();
			if (!reservationUserId) {
				return undefined;
			}

			try {
				const preview = await getRedemptionPreview({
					path: {
						reservationId: props.reservationId,
						reservationUserId,
					},
				});
				return { status: 'ready', preview };
			} catch (error) {
				if (isExpectedRedemptionPreviewUnavailable(error)) {
					return { status: 'unavailable' };
				}
				return { status: 'error', message: redemptionPreviewErrorMessage(error) };
			}
		},
		{ deferStream: true },
	);

	createEffect(() => {
		if (redemptionPreview.latest?.status === 'unavailable') {
			navigate(reservationHref(), { replace: true });
		}
	});

	return (
		<Switch>
			<Match when={!paymentReservationUserId()}>
				<InviteStepPanel class="gap-8 text-center">
					<InviteDecisionStep
						reservation={props.reservation}
						reservationId={props.reservationId}
						shareInviteId={props.shareInviteId}
						userId={props.userId}
						waiver={props.waiver}
						onComplete={() => navigate(reservationHref(), { replace: true })}
						onPaymentRequired={(reservationUserId) => setPaymentReservationUserId(reservationUserId)}
					/>
				</InviteStepPanel>
			</Match>
			<Match when={paymentReservationUserId()}>
				{(reservationUserId) => (
					<Suspense
						fallback={
							<InviteStepPanel>
								<PaymentOptionsLoading />
							</InviteStepPanel>
						}
					>
						<Show when={availableRedemptionPreview()}>
							{(previewState) => (
								<InviteStepPanel>
									<PaymentStep
										reservationId={props.reservationId}
										reservationUserId={reservationUserId()}
										redemptionPreview={redemptionPreviewForPayment(previewState())}
										redemptionPreviewError={redemptionPreviewErrorForPayment(previewState())}
										onRetry={() => setRedemptionPreviewRetry((retry) => retry + 1)}
										onComplete={() => navigate(reservationHref(), { replace: true })}
									/>
								</InviteStepPanel>
							)}
						</Show>
					</Suspense>
				)}
			</Match>
		</Switch>
	);
}

function InviteStepPanel(props: ParentProps<{ class?: string }>) {
	return <div class={`flex flex-col justify-center p-6 text-start md:p-10 ${props.class ?? ''}`}>{props.children}</div>;
}

const getReservationInvite = cachedGet('/v0/reservations/{reservationId}/join/{shareInviteId}');
const getRedemptionPreview = cachedGet('/v1/reservations/{reservationId}/users/{reservationUserId}/redemption-preview');

function isExpectedRedemptionPreviewUnavailable(error: unknown) {
	return error instanceof ApiError && [400, 403, 404].includes(error.statusCode);
}

function redemptionPreviewErrorMessage(error: unknown) {
	return error instanceof Error ? error.message : 'We could not load payment options. Please try again.';
}

function redemptionPreviewForPayment(state: AvailableRedemptionPreviewState) {
	return state.status === 'ready' ? state.preview : undefined;
}

function redemptionPreviewErrorForPayment(state: AvailableRedemptionPreviewState) {
	return state.status === 'error' ? state.message : undefined;
}

export const route = { info: { nav: { appearance: 'minimal', hero: true } } } satisfies RouteDefinition;
